import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import useSidebarStack from '@/common/sidebar/useSidebarStack.ts';

type SidebarSearchUpdate = {
  sidebarId?: string;
  sidebarParams?: string;
};

type SidebarSearchState = {
  id: string;
  params: Record<string, unknown>;
};

export function decodeSidebarSearchState(
  sidebarId: string,
  sidebarParams: string,
): SidebarSearchState | null {
  try {
    const parsedParams = JSON.parse(sidebarParams);

    if (
      !parsedParams ||
      typeof parsedParams !== 'object' ||
      Array.isArray(parsedParams)
    ) {
      return null;
    }

    return {
      id: sidebarId,
      params: parsedParams as Record<string, unknown>,
    };
  } catch {
    return null;
  }
}

export default function useSidebarSearchState() {
  const search = useSearch({ strict: false });
  const navigate = useNavigate();

  function set(next: SidebarSearchUpdate) {
    navigate({
      // @ts-expect-error this appears to be a non-issue?
      search: (prev) => ({
        ...prev,
        ...next,
      }),
    });
  }

  return {
    search,
    set,
  };
}

export function useApplySidebarSearchStateOnLoad() {
  const sidebarStack = useSidebarStack();
  const search = useSearch({ strict: false });
  const { sidebarId, sidebarParams } = search;

  useEffect(() => {
    if (typeof sidebarId !== 'string' || typeof sidebarParams !== 'string') {
      return;
    }

    const decodedSidebar = decodeSidebarSearchState(sidebarId, sidebarParams);
    if (!decodedSidebar) return;

    if (sidebarStack.getStack().length === 0) {
      sidebarStack.pushItemRaw(decodedSidebar.id, decodedSidebar.params);
    }
  }, [sidebarId, sidebarParams]);
}
